import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { toast } from 'sonner'

import { assignmentsApi } from '@/services/assignments'
import { jobsApi } from '@/services/jobs'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { CurrencyInput } from '@/components/common/currency-input'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Spinner } from '@/components/ui/spinner'
import { Switch } from '@/components/ui/switch'
import { getErrorMessage } from '@/lib/api'
import { formatShortCurrencyLabel } from '@/lib/currency'
import { getWorkdayEntryJobBlockedMessage } from '@/lib/job-status'
import { useOrganizationDateTimeFormat } from '@/lib/org-date-locale'
import {
  canEditWorkDayEntrySchedule,
  getWorkDayEntryScheduleEditBlockedMessage,
  getWorkDayEntryStatusBadgeClassName,
  getWorkDayEntryStatusHint,
  getWorkDayEntryStatusLabel,
  isFinalWorkDayEntryStatus,
} from '@/lib/workday-entry-status'
import {
  buildPlannedStartDateTime,
  buildPlannedStartOptions,
  buildReviewPayloadFromEntry,
  computeAttendanceFromLogged,
  computeHoursFromDateTimeRange,
  DEFAULT_PLANNED_DURATION,
  DEFAULT_PLANNED_START_TIME,
  extractDateFromDateTimeLocal,
  extractTimeFromDateTimeLocal,
  formatAttendancePreview,
  inferPlannedDuration,
  orgWallClockToApiDateTime,
  PLANNED_DURATION_OPTIONS,
  resolveAttendanceCheckInWallClock,
  resolveLoggedAttendancePreviewCheckOut,
  resolvePlannedEndFromStart,
  snapTimeToInterval,
  toEntryDateTimeLocalValue,
  toOptionalNumberString,
  toOrgWallClockDateTimeLocal,
  totalMinutesToLoggedDuration,
  validateCheckoutAgainstExistingCheckIn,
  wallClockToInstantMs,
} from '@/lib/workday-entry-schedule'
import { cn } from '@/lib/utils'
import { useAuthStore } from '@/stores/auth-store'

const scheduleFormSchema = z
  .object({
    planned_start: z.string().optional(),
    planned_end: z.string().optional(),
    payable_by_client: z.boolean().default(false),
    client_pay_amount: z.string().optional(),
    needs_supervisor_attention: z.boolean().default(false),
  })
  .superRefine((value, ctx) => {
    if (value.planned_start?.trim() && value.planned_end?.trim()) {
      const startDate = new Date(value.planned_start)
      const endDate = new Date(value.planned_end)
      if (
        !Number.isNaN(startDate.getTime())
        && !Number.isNaN(endDate.getTime())
        && startDate > endDate
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Planned start must be on or before the planned end time.',
          path: ['planned_end'],
        })
      }
    }
  })
  .refine(
    (value) => !value.payable_by_client || Boolean(value.client_pay_amount?.trim()),
    {
      message: 'Enter the one-time additional amount payable by the client.',
      path: ['client_pay_amount'],
    },
  )

type ScheduleFormData = z.infer<typeof scheduleFormSchema>

type WorkDayEntryScheduleDialogProps = {
  entryId: string
  jobId: string
  serviceInstanceId?: string | null
  open: boolean
  onOpenChange: (open: boolean) => void
  onSaved?: () => void | Promise<void>
}

const SummaryItem = ({ label, value }: { label: string; value: string }) => (
  <div className="space-y-1">
    <p className="text-xs text-muted-foreground">{label}</p>
    <p className="text-sm font-medium text-foreground">{value || '—'}</p>
  </div>
)

export function WorkDayEntryScheduleDialog({
  entryId,
  jobId,
  serviceInstanceId,
  open,
  onOpenChange,
  onSaved,
}: WorkDayEntryScheduleDialogProps) {
  const queryClient = useQueryClient()
  const currentBranch = useAuthStore((state) => state.currentBranch)
  const { locale: organizationDateLocale, timeZone: organizationTimeZoneFromHook } =
    useOrganizationDateTimeFormat(serviceInstanceId)

  const entryQuery = useQuery({
    queryKey: ['workday-entry', entryId],
    queryFn: () => assignmentsApi.getWorkDayEntry(entryId),
    enabled: open && Boolean(entryId),
  })

  const entry = entryQuery.data
  const organizationTimeZone = organizationTimeZoneFromHook
    || (typeof entry?.org_timezone === 'string' ? entry.org_timezone : undefined)
  const [plannedDuration, setPlannedDuration] = useState(DEFAULT_PLANNED_DURATION)
  const [loggedHours, setLoggedHours] = useState('0')
  const [loggedMinutes, setLoggedMinutes] = useState('0')
  const [actualStartDate, setActualStartDate] = useState('')
  const [actualStartTime, setActualStartTime] = useState(DEFAULT_PLANNED_START_TIME)
  const [isApplyAttendanceOpen, setIsApplyAttendanceOpen] = useState(false)
  const [attendanceDialogCheckIn, setAttendanceDialogCheckIn] = useState('')
  const [attendanceDialogCheckOut, setAttendanceDialogCheckOut] = useState('')
  const [pendingAttendanceUpdate, setPendingAttendanceUpdate] = useState<{
    checkIn: string
    checkOut: string
    hours: string
  } | null>(null)

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm<ScheduleFormData>({
    resolver: zodResolver(scheduleFormSchema),
    defaultValues: {
      planned_start: '',
      planned_end: '',
      payable_by_client: false,
      client_pay_amount: '',
      needs_supervisor_attention: false,
    },
  })

  const plannedStart = watch('planned_start')
  const plannedEnd = watch('planned_end')
  const payableByClient = watch('payable_by_client')

  const jobQuery = useQuery({
    queryKey: ['job', jobId, serviceInstanceId],
    queryFn: () => jobsApi.getJob(jobId, { service_instance_id: serviceInstanceId ?? undefined }),
    enabled: open && Boolean(jobId),
  })
  const workDayDate = entry?.work_date
    || extractDateFromDateTimeLocal(
      toEntryDateTimeLocalValue(entry?.planned_start, entry?.planned_start_local, organizationTimeZone),
    )
    || ''

  const plannedStartOptions = useMemo(
    () => buildPlannedStartOptions(workDayDate),
    [workDayDate],
  )

  const plannedStartValue = useMemo(() => {
    if (!workDayDate) {
      return ''
    }

    const snappedTime = snapTimeToInterval(extractTimeFromDateTimeLocal(plannedStart))
    return buildPlannedStartDateTime(workDayDate, snappedTime)
  }, [plannedStart, workDayDate])

  const actualStartDateTime = useMemo(() => {
    if (!actualStartDate?.trim() || !actualStartTime?.trim()) {
      return ''
    }

    return buildPlannedStartDateTime(actualStartDate, actualStartTime)
  }, [actualStartDate, actualStartTime])

  const savedAttendanceCheckIn = useMemo(
    () => toEntryDateTimeLocalValue(entry?.check_in, entry?.check_in_local, organizationTimeZone),
    [entry?.check_in, entry?.check_in_local, organizationTimeZone],
  )

  const savedAttendanceCheckOut = useMemo(
    () => toEntryDateTimeLocalValue(entry?.check_out, entry?.check_out_local, organizationTimeZone),
    [entry?.check_out, entry?.check_out_local, organizationTimeZone],
  )

  const loggedAttendancePreview = useMemo(
    () => computeAttendanceFromLogged({
      actualStart: actualStartDateTime,
      loggedHours,
      loggedMinutes,
      timeZone: organizationTimeZone,
    }),
    [actualStartDateTime, loggedHours, loggedMinutes, organizationTimeZone],
  )

  const attendanceDialogError = useMemo(
    () => validateCheckoutAgainstExistingCheckIn({
      checkInWallClock: attendanceDialogCheckIn,
      checkInUtc: entry?.check_in,
      checkOutWallClock: attendanceDialogCheckOut,
      timeZone: organizationTimeZone,
      assumeExistingCheckIn: entry?.status === 'in_progress' || entry?.status === 'logged',
    }),
    [attendanceDialogCheckIn, attendanceDialogCheckOut, entry?.check_in, entry?.status, organizationTimeZone],
  )

  const loggedAttendancePreviewCheckOut = useMemo(
    () => resolveLoggedAttendancePreviewCheckOut({
      computedCheckOut: loggedAttendancePreview.checkOut,
      savedCheckOut: savedAttendanceCheckOut,
      checkOutUtc: entry?.check_out,
      checkOutLocal: entry?.check_out_local,
      timeZone: organizationTimeZone,
    }),
    [
      entry?.check_out,
      entry?.check_out_local,
      loggedAttendancePreview.checkOut,
      organizationTimeZone,
      savedAttendanceCheckOut,
    ],
  )

  const payCurrencyLabel = formatShortCurrencyLabel({
    symbol: currentBranch?.currencySymbol,
    code: currentBranch?.currencyCode,
  })

  const formatDisplayMoney = (value?: string | null) => {
    if (!value || !value.trim()) {
      return '-'
    }

    const numeric = Number(value)
    if (!Number.isFinite(numeric)) {
      return value
    }

    const amount = numeric.toLocaleString(undefined, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    })

    return payCurrencyLabel && payCurrencyLabel !== '-' ? `${amount} ${payCurrencyLabel}` : amount
  }

  const jobBlockedMessage = useMemo(
    () => getWorkdayEntryJobBlockedMessage(jobQuery.data),
    [jobQuery.data],
  )

  const scheduleBlockedMessage = useMemo(() => {
    if (!entry?.status) {
      return null
    }

    if (!canEditWorkDayEntrySchedule(entry.status)) {
      return getWorkDayEntryScheduleEditBlockedMessage(entry.status)
    }

    return null
  }, [entry?.status])

  const isBlocked = Boolean(jobBlockedMessage || scheduleBlockedMessage)

  useEffect(() => {
    if (!open || !entry) {
      return
    }

    reset({
      planned_start: toEntryDateTimeLocalValue(entry.planned_start, entry.planned_start_local, organizationTimeZone),
      planned_end: toEntryDateTimeLocalValue(entry.planned_end, entry.planned_end_local, organizationTimeZone),
      payable_by_client: Boolean(entry.payable_by_client),
      client_pay_amount: toOptionalNumberString(entry.client_pay_amount),
      needs_supervisor_attention: Boolean(entry.needs_supervisor_attention),
    })

    setPlannedDuration(
      inferPlannedDuration(
        toEntryDateTimeLocalValue(entry.planned_start, entry.planned_start_local, organizationTimeZone),
        toEntryDateTimeLocalValue(entry.planned_end, entry.planned_end_local, organizationTimeZone),
      ),
    )

    const checkIn = toEntryDateTimeLocalValue(entry.check_in, entry.check_in_local, organizationTimeZone)
    const checkOut = toEntryDateTimeLocalValue(entry.check_out, entry.check_out_local, organizationTimeZone)
    const plannedStartValue = toEntryDateTimeLocalValue(entry.planned_start, entry.planned_start_local, organizationTimeZone)
    const workDate = entry.work_date || extractDateFromDateTimeLocal(plannedStartValue)
    const defaultTime = extractTimeFromDateTimeLocal(checkIn || plannedStartValue) || DEFAULT_PLANNED_START_TIME

    const nextActualStartDate = checkIn
      ? (extractDateFromDateTimeLocal(checkIn) || workDate)
      : workDate
    const nextActualStartTime = checkIn
      ? (extractTimeFromDateTimeLocal(checkIn) || DEFAULT_PLANNED_START_TIME)
      : defaultTime

    setActualStartDate(nextActualStartDate)
    setActualStartTime(nextActualStartTime)
    setPendingAttendanceUpdate(null)

    let nextLoggedHours = '0'
    let nextLoggedMinutes = '0'

    if (checkIn && checkOut) {
      const startMs = wallClockToInstantMs(checkIn, organizationTimeZone)
      const endMs = wallClockToInstantMs(checkOut, organizationTimeZone)
      if (Number.isFinite(startMs) && Number.isFinite(endMs) && endMs > startMs) {
        const diffMinutes = Math.round((endMs - startMs) / 60_000)
        const duration = totalMinutesToLoggedDuration(diffMinutes)
        nextLoggedHours = duration.hours
        nextLoggedMinutes = duration.minutes
      }
    } else if (checkIn) {
      nextLoggedHours = '0'
      nextLoggedMinutes = '0'
    }

    setLoggedHours(nextLoggedHours)
    setLoggedMinutes(nextLoggedMinutes)
  }, [entry, open, organizationTimeZone, reset])

  useEffect(() => {
    if (!plannedStart?.trim()) {
      return
    }

    const endDate = resolvePlannedEndFromStart(plannedStart, plannedDuration)
    if (endDate) {
      setValue('planned_end', endDate, { shouldValidate: true })
    }
  }, [plannedDuration, plannedStart, setValue])

  const openAttendanceDialog = (checkIn: string, checkOut: string) => {
    const resolvedCheckIn = checkIn
      || savedAttendanceCheckIn
      || actualStartDateTime
      || plannedStartValue
      || plannedStart
      || ''

    setAttendanceDialogCheckIn(resolvedCheckIn)
    setAttendanceDialogCheckOut(checkOut)
    setIsApplyAttendanceOpen(true)
  }

  const handleOpenApplyPlannedAttendance = () => {
    const plannedStartForDialog = plannedStartValue || plannedStart || ''
    const plannedEndForDialog = resolvePlannedEndFromStart(plannedStartForDialog, plannedDuration)
      || plannedEnd
      || ''

    openAttendanceDialog(plannedStartForDialog, plannedEndForDialog)
  }

  const handleOpenApplyLoggedAttendance = () => {
    const preview = computeAttendanceFromLogged({
      actualStart: actualStartDateTime,
      loggedHours,
      loggedMinutes,
      timeZone: organizationTimeZone,
    })

    openAttendanceDialog(preview.checkIn, preview.checkOut)
  }

  const handleOpenSetCheckoutNow = () => {
    const checkIn = resolveAttendanceCheckInWallClock({
      checkInUtc: entry?.check_in,
      checkInLocal: entry?.check_in_local,
      timeZone: organizationTimeZone,
      fallback: actualStartDateTime,
    })
    const checkOut = toOrgWallClockDateTimeLocal(new Date(), organizationTimeZone)

    if (!checkIn.trim() && !entry?.check_in?.trim()) {
      toast.error('Set a check-in time before recording check-out.')
      return
    }

    openAttendanceDialog(checkIn, checkOut)
  }

  const handleConfirmAttendanceUpdate = () => {
    const validationError = validateCheckoutAgainstExistingCheckIn({
      checkInWallClock: attendanceDialogCheckIn,
      checkInUtc: entry?.check_in,
      checkOutWallClock: attendanceDialogCheckOut,
      timeZone: organizationTimeZone,
      assumeExistingCheckIn: entry?.status === 'in_progress' || entry?.status === 'logged',
    })
    if (validationError) {
      toast.error(validationError)
      return
    }

    const checkIn = attendanceDialogCheckIn.trim()
      || toEntryDateTimeLocalValue(entry?.check_in, entry?.check_in_local, organizationTimeZone)
    const checkOut = attendanceDialogCheckOut.trim()
    const hours = computeHoursFromDateTimeRange(checkIn, checkOut, organizationTimeZone)

    setPendingAttendanceUpdate({ checkIn, checkOut, hours })

    if (checkIn) {
      setActualStartDate(extractDateFromDateTimeLocal(checkIn))
      setActualStartTime(extractTimeFromDateTimeLocal(checkIn) || DEFAULT_PLANNED_START_TIME)
    }

    if (checkIn && checkOut) {
      const startMs = wallClockToInstantMs(checkIn, organizationTimeZone)
      const endMs = wallClockToInstantMs(checkOut, organizationTimeZone)
      if (Number.isFinite(startMs) && Number.isFinite(endMs) && endMs >= startMs) {
        const diffMinutes = Math.round((endMs - startMs) / 60_000)
        const duration = totalMinutesToLoggedDuration(diffMinutes)
        setLoggedHours(duration.hours)
        setLoggedMinutes(duration.minutes)
      }
    }

    setIsApplyAttendanceOpen(false)
    toast.success('Check-in and check-out will update when you save.')
  }

  const saveMutation = useMutation({
    mutationFn: async (data: ScheduleFormData) => {
      if (!entry) {
        throw new Error('Work day entry not loaded.')
      }

      const submitWorkDate = workDayDate || ''
      const plannedStartForSubmit = submitWorkDate
        ? buildPlannedStartDateTime(
            submitWorkDate,
            snapTimeToInterval(extractTimeFromDateTimeLocal(data.planned_start)),
          )
        : data.planned_start?.trim() || ''
      const plannedEndForSubmit = resolvePlannedEndFromStart(
        plannedStartForSubmit,
        plannedDuration,
      )

      const existingCheckIn = toEntryDateTimeLocalValue(entry.check_in, entry.check_in_local, organizationTimeZone)
      const existingCheckOut = toEntryDateTimeLocalValue(entry.check_out, entry.check_out_local, organizationTimeZone)
      const existingHours = toOptionalNumberString(entry.hours) || '0.00'

      let checkInForSubmit = existingCheckIn
      let checkOutForSubmit = existingCheckOut
      let hoursForSubmit = existingHours

      if (pendingAttendanceUpdate) {
        checkInForSubmit = pendingAttendanceUpdate.checkIn
        checkOutForSubmit = pendingAttendanceUpdate.checkOut
        hoursForSubmit = pendingAttendanceUpdate.hours
      }

      return assignmentsApi.updateWorkDayEntry(entryId, {
        job: entry.job,
        work_day: entry.work_day,
        worker: entry.worker,
        worker_skill: entry.worker_skill || undefined,
        planned_start: orgWallClockToApiDateTime(plannedStartForSubmit, organizationTimeZone),
        planned_end: orgWallClockToApiDateTime(plannedEndForSubmit, organizationTimeZone),
        check_in: orgWallClockToApiDateTime(checkInForSubmit, organizationTimeZone),
        check_out: orgWallClockToApiDateTime(checkOutForSubmit, organizationTimeZone),
        hours: hoursForSubmit,
        pay_hourly_rate: entry.pay_hourly_rate ?? undefined,
        pay_flat: entry.pay_flat ?? undefined,
        night_pay: entry.night_pay ?? undefined,
        holiday_pay: entry.holiday_pay ?? undefined,
        peak_pay: entry.peak_pay ?? undefined,
        special_pay: entry.special_pay ?? undefined,
        payable_by_client: data.payable_by_client,
        client_pay_amount: data.payable_by_client ? (data.client_pay_amount?.trim() || undefined) : undefined,
        needs_supervisor_attention: Boolean(data.needs_supervisor_attention),
        notes: entry.notes || undefined,
        ...buildReviewPayloadFromEntry(entry),
      })
    },
    onSuccess: async () => {
      toast.success('Entry schedule updated successfully')
      await queryClient.invalidateQueries({ queryKey: ['workday-entries'] })
      await queryClient.invalidateQueries({ queryKey: ['workdays'] })
      await queryClient.invalidateQueries({ queryKey: ['workday-entry', entryId] })
      await queryClient.invalidateQueries({ queryKey: ['workday-entry-audits', entryId] })
      await queryClient.invalidateQueries({ queryKey: ['job-work-day-entries'] })
      await queryClient.invalidateQueries({ queryKey: ['worker-work-day-entries'] })
      await queryClient.invalidateQueries({ queryKey: ['assignment-workday-entries'] })
      await queryClient.invalidateQueries({ queryKey: ['attention-work-day-entries'] })
      await onSaved?.()
      onOpenChange(false)
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })

  const onSubmit = (data: ScheduleFormData) => {
    saveMutation.mutate(data)
  }

  const statusLabel = entry?.status_label || getWorkDayEntryStatusLabel(entry?.status)
  const statusHint = entry?.status_hint || getWorkDayEntryStatusHint(entry?.status)
  const statusIsFinal = entry?.status_is_final ?? isFinalWorkDayEntryStatus(entry?.status)

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="z-[130] flex h-[min(90vh,calc(100vh-2rem))] w-[calc(100vw-2rem)] max-w-3xl flex-col gap-0 overflow-hidden p-0 sm:max-w-3xl">
          <DialogHeader className="border-b border-border/60 px-6 py-4">
            <DialogTitle>Edit schedule</DialogTitle>
            <DialogDescription>
              Update planned times and attendance without leaving the workdays page.
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto px-6 py-4">
            {entryQuery.isLoading || jobQuery.isLoading ? (
              <div className="flex h-40 items-center justify-center">
                <Spinner className="mr-2" />
                <span className="text-sm text-muted-foreground">Loading entry...</span>
              </div>
            ) : entryQuery.isError || !entry ? (
              <p className="text-sm text-destructive">Could not load this work day entry.</p>
            ) : isBlocked ? (
              <p className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                {scheduleBlockedMessage || jobBlockedMessage}
              </p>
            ) : (
              <form id="workday-schedule-dialog-form" onSubmit={handleSubmit(onSubmit)} className="space-y-5">
                <div className="rounded-xl border border-border/70 bg-muted/20 p-4">
                  <div className="grid gap-4 sm:grid-cols-2">
                    <SummaryItem
                      label="Job"
                      value={entry.job_title || `Job #${entry.job}`}
                    />
                    <SummaryItem
                      label="Worker"
                      value={entry.worker_name || `Worker #${entry.worker}`}
                    />
                    <SummaryItem label="Work day" value={workDayDate || '—'} />
                    <SummaryItem
                      label="Skill"
                      value={entry.worker_skill_label || (entry.worker_skill ? `Skill #${entry.worker_skill}` : '—')}
                    />
                    <div className="space-y-1 sm:col-span-2">
                      <p className="text-xs text-muted-foreground">Status</p>
                      <div className="flex flex-wrap items-center gap-2">
                        <Badge
                          variant="outline"
                          className={cn('font-medium', getWorkDayEntryStatusBadgeClassName(entry.status))}
                        >
                          {statusLabel}
                        </Badge>
                        {statusIsFinal ? (
                          <Badge variant="outline" className="border-teal-300 bg-teal-50 text-teal-800">
                            Final
                          </Badge>
                        ) : null}
                      </div>
                      {statusHint ? <p className="mt-1 text-xs text-muted-foreground">{statusHint}</p> : null}
                    </div>
                  </div>
                </div>

                <div className="rounded-xl border border-border/70 bg-muted/20 p-4">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <p className="text-sm font-medium text-foreground">Client payable</p>
                      <p className="text-xs text-muted-foreground">
                        {payableByClient ? 'Billable to the client.' : 'Internal only.'}
                      </p>
                    </div>
                    <Switch
                      checked={payableByClient}
                      onCheckedChange={(checked) => {
                        setValue('payable_by_client', checked, { shouldDirty: true, shouldValidate: true })
                        if (!checked) {
                          setValue('client_pay_amount', '', { shouldDirty: true, shouldValidate: true })
                        }
                      }}
                    />
                  </div>
                  {payableByClient ? (
                    <div className="mt-3 space-y-2">
                      <Label htmlFor="schedule_dialog_client_pay_amount">Client amount</Label>
                      <CurrencyInput id="schedule_dialog_client_pay_amount" {...register('client_pay_amount')} />
                      {errors.client_pay_amount ? (
                        <p className="text-sm text-destructive">{errors.client_pay_amount.message}</p>
                      ) : null}
                    </div>
                  ) : (
                    <p className="mt-3 text-sm text-muted-foreground">
                      Current: {formatDisplayMoney(toOptionalNumberString(entry.client_pay_amount))}
                    </p>
                  )}
                </div>

                <div className="space-y-4">
                  <p className="text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                    Planned schedule
                  </p>

                  <input type="hidden" {...register('planned_start')} />

                  <div className="space-y-2">
                    <Label htmlFor="schedule_dialog_planned_start">Planned start</Label>
                    <Select
                      value={plannedStartValue || undefined}
                      onValueChange={(value) => {
                        setValue('planned_start', value, { shouldDirty: true, shouldValidate: true })
                      }}
                      disabled={!workDayDate || plannedStartOptions.length === 0}
                    >
                      <SelectTrigger id="schedule_dialog_planned_start" aria-invalid={!!errors.planned_start}>
                        <SelectValue placeholder={workDayDate ? 'Select time' : 'No work day'} />
                      </SelectTrigger>
                      <SelectContent>
                        {plannedStartOptions.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {errors.planned_start ? (
                      <p className="text-sm text-destructive">{errors.planned_start.message}</p>
                    ) : null}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="schedule_dialog_planned_duration">Duration</Label>
                    <Select value={plannedDuration} onValueChange={setPlannedDuration}>
                      <SelectTrigger id="schedule_dialog_planned_duration" className="w-full">
                        <SelectValue placeholder="Select duration" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectGroup>
                          <SelectLabel>Minutes</SelectLabel>
                          {PLANNED_DURATION_OPTIONS.filter((option) => option.group === 'minutes').map((option) => (
                            <SelectItem key={option.value} value={option.value}>
                              {option.label}
                            </SelectItem>
                          ))}
                        </SelectGroup>
                        <SelectGroup>
                          <SelectLabel>Hours</SelectLabel>
                          {PLANNED_DURATION_OPTIONS.filter((option) => option.group === 'hours').map((option) => (
                            <SelectItem key={option.value} value={option.value}>
                              {option.label}
                            </SelectItem>
                          ))}
                        </SelectGroup>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="schedule_dialog_planned_end">Planned end</Label>
                    <Input
                      id="schedule_dialog_planned_end"
                      type="datetime-local"
                      className="w-full"
                      {...register('planned_end')}
                      aria-invalid={!!errors.planned_end}
                    />
                    {errors.planned_end ? (
                      <p className="text-sm text-destructive">{errors.planned_end.message}</p>
                    ) : null}
                  </div>
                </div>

                <div className="space-y-4 border-t border-border/60 pt-4">
                  <p className="text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                    Log attendance
                  </p>

                  <div className="flex flex-wrap items-center gap-2">
                    <Button type="button" variant="outline" size="sm" onClick={handleOpenApplyPlannedAttendance}>
                      Set from planned
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={handleOpenApplyLoggedAttendance}
                      disabled={!actualStartDateTime.trim()}
                    >
                      Set from logged times
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={handleOpenSetCheckoutNow}
                      disabled={!savedAttendanceCheckIn && !actualStartDateTime.trim()}
                    >
                      Check-out now
                    </Button>
                  </div>

                  {pendingAttendanceUpdate ? (
                    <PendingAttendanceBanner
                      checkIn={pendingAttendanceUpdate.checkIn}
                      checkOut={pendingAttendanceUpdate.checkOut}
                      locale={organizationDateLocale}
                      timeZone={organizationTimeZone}
                      onCancel={() => setPendingAttendanceUpdate(null)}
                    />
                  ) : savedAttendanceCheckIn || savedAttendanceCheckOut ? (
                    <CurrentAttendanceBanner
                      checkIn={savedAttendanceCheckIn}
                      checkOut={savedAttendanceCheckOut}
                      locale={organizationDateLocale}
                      timeZone={organizationTimeZone}
                    />
                  ) : null}

                  <div className="grid gap-4 sm:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor="schedule_dialog_actual_start_date">Start date</Label>
                      <Input
                        id="schedule_dialog_actual_start_date"
                        type="date"
                        value={actualStartDate}
                        onChange={(event) => {
                          setPendingAttendanceUpdate(null)
                          setActualStartDate(event.target.value)
                        }}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="schedule_dialog_actual_start_time">Start time</Label>
                      <Input
                        id="schedule_dialog_actual_start_time"
                        type="time"
                        value={actualStartTime}
                        onChange={(event) => {
                          setPendingAttendanceUpdate(null)
                          setActualStartTime(event.target.value)
                        }}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="schedule_dialog_logged_hours">Hours logged</Label>
                      <Input
                        id="schedule_dialog_logged_hours"
                        type="number"
                        min={0}
                        max={24}
                        step={1}
                        value={loggedHours}
                        onChange={(event) => {
                          setPendingAttendanceUpdate(null)
                          setLoggedHours(String(Math.max(0, Number(event.target.value) || 0)))
                        }}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="schedule_dialog_logged_minutes">Minutes logged</Label>
                      <Input
                        id="schedule_dialog_logged_minutes"
                        type="number"
                        min={0}
                        max={59}
                        step={1}
                        value={loggedMinutes}
                        onChange={(event) => {
                          setPendingAttendanceUpdate(null)
                          setLoggedMinutes(String(Math.max(0, Math.min(59, Number(event.target.value) || 0))))
                        }}
                      />
                    </div>
                  </div>

                  {loggedAttendancePreview?.checkIn && !pendingAttendanceUpdate ? (
                    <div className="rounded-xl border border-dashed border-border/60 bg-muted/10 px-4 py-3 text-sm">
                      <p className="text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                        Logged times preview
                      </p>
                      <p className="mt-2 text-foreground">
                        Check-in: {formatAttendancePreview(loggedAttendancePreview.checkIn, organizationDateLocale, organizationTimeZone)}
                      </p>
                      <p className="text-foreground">
                        Check-out:{' '}
                        {formatAttendancePreview(
                          loggedAttendancePreviewCheckOut,
                          organizationDateLocale,
                          organizationTimeZone,
                        )}
                      </p>
                    </div>
                  ) : null}
                </div>

                <div className="flex items-center justify-between rounded-xl border border-border/60 bg-muted/20 px-4 py-3">
                  <div>
                    <p className="text-sm font-medium text-foreground">Supervisor attention</p>
                    <p className="text-xs text-muted-foreground">Flag this entry for review.</p>
                  </div>
                  <Switch
                    checked={watch('needs_supervisor_attention')}
                    onCheckedChange={(checked) => {
                      setValue('needs_supervisor_attention', checked, { shouldDirty: true })
                    }}
                  />
                </div>
              </form>
            )}
          </div>

          <DialogFooter className="border-t border-border/60 px-6 py-4">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button
              type="submit"
              form="workday-schedule-dialog-form"
              disabled={isBlocked || saveMutation.isPending || entryQuery.isLoading}
            >
              {saveMutation.isPending ? <Spinner className="mr-2" /> : null}
              Save schedule
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={isApplyAttendanceOpen} onOpenChange={setIsApplyAttendanceOpen}>
        <AlertDialogContent className="z-[140]">
          <AlertDialogHeader>
            <AlertDialogTitle>Update check-in and check-out</AlertDialogTitle>
            <AlertDialogDescription>
              Confirm the attendance times to apply. Times use the organization timezone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="grid gap-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="schedule_attendance_check_in">Check-in</Label>
              <Input
                id="schedule_attendance_check_in"
                type="datetime-local"
                value={attendanceDialogCheckIn}
                onChange={(event) => setAttendanceDialogCheckIn(event.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="schedule_attendance_check_out">Check-out</Label>
              <div className="flex gap-2">
                <Input
                  id="schedule_attendance_check_out"
                  type="datetime-local"
                  className="flex-1"
                  value={attendanceDialogCheckOut}
                  onChange={(event) => setAttendanceDialogCheckOut(event.target.value)}
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setAttendanceDialogCheckOut(
                      toOrgWallClockDateTimeLocal(new Date(), organizationTimeZone),
                    )
                  }}
                >
                  Now
                </Button>
              </div>
            </div>
            {attendanceDialogError ? (
              <p className="text-sm text-destructive">{attendanceDialogError}</p>
            ) : null}
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmAttendanceUpdate}
              disabled={Boolean(attendanceDialogError)}
            >
              Apply times
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}

function PendingAttendanceBanner({
  checkIn,
  checkOut,
  locale,
  timeZone,
  onCancel,
}: {
  checkIn: string
  checkOut: string
  locale: string
  timeZone?: string
  onCancel: () => void
}) {
  return (
    <div className="rounded-xl border border-primary/30 bg-primary/5 px-4 py-3 text-sm">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.14em] text-primary">
            Pending attendance update
          </p>
          <p className="mt-2 text-foreground">
            Check-in: {formatAttendancePreview(checkIn, locale, timeZone)}
          </p>
          <p className="text-foreground">
            Check-out: {checkOut ? formatAttendancePreview(checkOut, locale, timeZone) : '—'}
          </p>
        </div>
        <Button type="button" variant="ghost" size="sm" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </div>
  )
}

function CurrentAttendanceBanner({
  checkIn,
  checkOut,
  locale,
  timeZone,
}: {
  checkIn: string
  checkOut: string
  locale: string
  timeZone?: string
}) {
  return (
    <div className="rounded-xl border border-border/60 bg-background/80 px-4 py-3 text-sm">
      <p className="text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">
        Current attendance
      </p>
      <p className="mt-2 text-foreground">
        Check-in: {formatAttendancePreview(checkIn, locale, timeZone)}
      </p>
      <p className="text-foreground">
        Check-out: {checkOut ? formatAttendancePreview(checkOut, locale, timeZone) : '—'}
      </p>
    </div>
  )
}
